import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import DynamicInput from '../DynamicInputs/dynamicInput.js';
import addIcon from '../../../Assets/images/svg/dar_add_circle.svg';
import addIconDisabled from '../../../Assets/images/svg/dar_add_circle_disabled.svg';
import styles from './category.module.scss';
import { fetchKpiMasterDetails } from './categoryUtil.js';

const Category = ({
  id,
  title,
  targetFormatList,
  updateKpiDetails,
  dropdownData,
  isEdit,
  selectedKpis, // Receive selected KPIs
  setSelectedKpis,
  onKpiChange, // Receive KPI change handler
}) => {
  const [kpiList, setKpiList] = useState([]);
  const [inputRowList, setInputRowList] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const kpiData = await fetchKpiMasterDetails(id);
        setKpiList(kpiData);
        if (isEdit && dropdownData) {
          let index = 0;
          const initialInputRows = dropdownData?.map((option) => ({
            id: ++index,
            kpiId: option.kpiName,
            targetFormatId: option.targetFormat,
            symbol: option.targetFormat !== 'Yes/No' ? option.targetFormatValue : '',
            symbolTargetValue: option.targetValue,
            targetValue: option.targetFormat === 'Yes/No' ? option.targetFormatValue : '',
            kpiFormatOpertorId: option.kpiFormatOpertorId,
            kpiNameId: option.kpiNameId,
          }));
          setInputRowList(initialInputRows);
        } else {
          setInputRowList([]);
        }
      } catch (err) {
        console.error('Failed to fetch KPI data:', err);
      }
    };
    fetchData();
  }, [isEdit, id, dropdownData]);

  useEffect(() => {
    updateKpiDetails({ id, kpis: inputRowList });
  }, [inputRowList, id, updateKpiDetails]);

  const addRow = useCallback(() => {
    setInputRowList((prevInputRowList) => {
      const inputRow = {
        kpiId: '',
        targetFormatId: '',
        symbol: '',
        symbolTargetValue: '',
        targetValue: '',
      };

      if (!prevInputRowList?.length) {
        inputRow.id = 1;
      } else {
        inputRow.id = prevInputRowList[prevInputRowList.length - 1].id + 1;
      }

      return prevInputRowList?.concat([inputRow]);
    });
  }, []);

  const removeRow = useCallback((rowId) => {
    setInputRowList((prevInputRowList) => {
      return prevInputRowList.filter((inputRow) => inputRow.id !== rowId);
    });
  }, []);

  const inputChangeHandler = useCallback(({ rowId, key, value }) => {
    setInputRowList((prevInputRowList) => {
      return prevInputRowList.map((inputRow) => {
        if (inputRow.id === rowId) {
          return {
            ...inputRow,
            [key]: value,
          };
        } else {
          return inputRow;
        }
      });
    });
    if (key === 'kpiId') {
      onKpiChange(rowId, value, id); // Update selected KPIs
    }
  }, [onKpiChange]);

  return (
    <article className={styles.category}>
      <header>
        <h3 className={styles.title}>{title}</h3>
        <button
          type="button"
          onClick={addRow}
          disabled={inputRowList.length >= kpiList.length}
        >
          <img src={inputRowList.length >= kpiList.length ? addIconDisabled : addIcon}
            alt="add icon" />
        </button>
      </header>
      <DynamicInput
        categoryId={id}
        inputRowList={inputRowList}
        removeRow={removeRow}
        inputChangeHandler={inputChangeHandler}
        kpiList={kpiList}
        targetFormatList={targetFormatList}
        dropdownData={dropdownData}
        isEdit={isEdit}
        selectedKpis={selectedKpis}
        setSelectedKpis={setSelectedKpis}
      />
    </article>
  );
};

Category.propTypes = {
  id: PropTypes.number,
  title: PropTypes.string,
  targetFormatList: PropTypes.array,
  updateKpiDetails: PropTypes.func,
  dropdownData: PropTypes.array,
  isEdit: PropTypes.bool,
  selectedKpis: PropTypes.array,
  setSelectedKpis: PropTypes.func,
  onKpiChange: PropTypes.func,
};

export default Category;
